import React, { useLayoutEffect, useState } from 'react';
import { Renderer } from '@/pages/reports/components';
import type { Report } from '@/pages/reports/schemas';
import { normalizeHeroFirst } from '@/pages/reports/utils/heroOrder';
import { Footer } from '@/pages/reports/components/ui';
import { ReportShareBar } from './ReportShareBar';
import {
  FOOTER_SECTION_ANALYTICS,
  resolveSectionAnalytics,
} from '@/lib/reportAnalyticsSections';
import { markReportFirstContent } from '@/lib/reportPerformance';
import { ReportI18nProvider } from '@/pages/reports/i18n';
import { usePostHogReportEngagement } from '@/hooks/use-posthog-report-engagement';

interface ReportContentProps {
  report: Report;
  analyticsEnabled?: boolean;
}

export const ReportContent: React.FC<ReportContentProps> = ({
  report,
  analyticsEnabled = true,
}) => {
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const orderedContent = normalizeHeroFirst((report.content || []).filter((c) => c.type !== 'footer'));

  useLayoutEffect(() => {
    if (!rootEl) return;
    markReportFirstContent(report.id);
  }, [report.id, rootEl]);

  usePostHogReportEngagement({
    enabled: analyticsEnabled,
    reportId: report.id,
    root: rootEl,
  });

  return (
    <ReportI18nProvider locale={report.locale ?? 'pt'}>
      <div ref={setRootEl} data-report-id={report.id} className="space-y-0 font-['Times_New_Roman']">
        {orderedContent.map((component) => {
          const analytics = resolveSectionAnalytics(component);
          const ownsSectionTracking = component.type === 'thematic-session';

          return (
            <div
              key={component.id}
              {...(ownsSectionTracking
                ? {}
                : {
                    'data-report-section': component.id || component.type,
                    'data-report-section-name': analytics.sectionName,
                    'data-report-link-event': analytics.linkEvent,
                    'data-report-special-name': analytics.specialName,
                  })}
            >
              <Renderer component={component} />
            </div>
          );
        })}
        <div
          data-report-section="footer"
          data-report-section-name={FOOTER_SECTION_ANALYTICS.sectionName}
          data-report-link-event={FOOTER_SECTION_ANALYTICS.linkEvent}
        >
          <Footer />
        </div>
        <ReportShareBar reportId={report.id} title={report.title} />
      </div>
    </ReportI18nProvider>
  );
};
